import { Button, Input, Switch, Text, Textarea } from "@medusajs/ui"
import { storefrontUrl } from "@/lib/storefront-url"
import { toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Field } from "@/components/form"
import { FilePicker } from "@/components/file-picker"
import { ErrorState, PageHeader, SaveBar, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useOnlineStore, useSaveOnlineStore } from "@/lib/settings"
import { sdk } from "@/lib/sdk"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"


type Form = { title: string; meta_description: string; social_image: string; password_enabled: boolean; password_message: string; password: string }

/** Sales channels → Online Store → Preferences (title, meta description, social image, password page). */
export function OnlineStorePage() {
  const { data, error } = useOnlineStore()
  const save = useSaveOnlineStore()
  const writable = useCan()("settings:write")
  const url = storefrontUrl()
  const [base, setBase] = useState<Form | null>(null)
  const [f, setF] = useState<Form | null>(null)
  const [pickingImage, setPickingImage] = useState(false)
  useEffect(() => {
    if (data) {
      const { password_set: _p, ...rest } = data.settings
      setBase({ ...rest, password: "" })
      setF({ ...rest, password: "" })
    }
  }, [data])
  if (error) return <ErrorState error={error} />
  if (!f || !base || !data) return <Text size="small">Loading…</Text>
  const set = (p: Partial<Form>) => setF({ ...f, ...p })
  const dirty = JSON.stringify(f) !== JSON.stringify(base)
  const imageBad = f.social_image !== "" && !/^(https:\/\/|\/(?!\/))/i.test(f.social_image)
  const pwMissing = f.password_enabled && !data.settings.password_set && f.password.length < 6
  const pwShort = f.password !== "" && f.password.length < 6
  const doSave = () => {
    const { password, ...rest } = f
    save.mutate({ ...rest, ...(password ? { password } : {}) }, { onSuccess: () => toast.success("Preferences saved"), onError: (e) => toast.error(errorMessage(e)) })
  }
  const title = f.title || "Your store name"
  return (
    <>
      <PageHeader
        title="Online Store"
        actions={<><Link to="/online-store/customize" className="txt-compact-small-plus rounded-lg border border-ui-border-base bg-ui-bg-base px-3 py-1.5 hover:bg-ui-bg-base-hover">Customize</Link> <a href={url} target="_blank" rel="noreferrer" className="txt-compact-small-plus rounded-lg bg-[color:var(--k-solid)] px-3 py-1.5 text-[color:var(--k-on-solid)] hover:bg-[color:var(--k-solid-hover)]">View your store</a></>}
      />
      <div className="mx-auto flex max-w-3xl flex-col gap-4 pb-24">
        <Section title="Title and meta description">
          <div className="flex flex-col gap-3 p-4">
            <Text size="small" className="text-ui-fg-subtle">The title and meta description help define how your store shows up on search engines.</Text>
            <Field label="Homepage title" hint={`${f.title.length} of 70 characters used`}><Input value={f.title} maxLength={70} disabled={!writable} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="Homepage meta description" hint={`${f.meta_description.length} of 320 characters used`}><Textarea rows={3} value={f.meta_description} maxLength={320} disabled={!writable} onChange={(e) => set({ meta_description: e.target.value })} /></Field>
            <div className="rounded-lg border border-ui-border-base p-3" aria-label="Search engine preview">
              <div className="truncate text-[18px] text-[color:var(--k-serp-title)]">{title}</div>
              <div className="truncate text-[13px] text-[color:var(--k-serp-url)]">{url}</div>
              <div className="line-clamp-2 text-[13px] text-ui-fg-subtle">{f.meta_description || "Add a meta description to control how your store appears in search results."}</div>
            </div>
          </div>
        </Section>
        <Section title="Social sharing image">
          <div className="flex flex-col gap-3 p-4">
            <Field label="Image URL" hint={imageBad ? "Use an https:// link or a /path on your store" : "Shown when your store is shared on social media. Recommended 1200 × 628 px."}>
              <Input value={f.social_image} maxLength={500} placeholder="https://…" disabled={!writable} onChange={(e) => set({ social_image: e.target.value.trim() })} aria-invalid={imageBad} />
            </Field>
            {f.social_image && !imageBad && <img src={f.social_image} alt="" className="max-h-40 rounded-lg border border-ui-border-base object-cover" />}
            {writable && <div><Button type="button" size="small" variant="secondary" onClick={() => setPickingImage(true)}>Select from Files</Button></div>}
            {pickingImage && <FilePicker title="Select image" onClose={() => setPickingImage(false)} onDone={([file]) => { setPickingImage(false); set({ social_image: file.url }) }} />}
          </div>
        </Section>
        <Section title="Password protection">
          <div className="flex flex-col gap-3 p-4">
            <label className="flex items-center gap-3 text-[13px]">
              <Switch checked={f.password_enabled} disabled={!writable} onCheckedChange={(v) => set({ password_enabled: v })} aria-label="Restrict access with a password" />
              Restrict access to visitors with the password
            </label>
            {f.password_enabled && (
              <>
                <Text size="small" className="text-ui-fg-subtle">Visitors see a password page instead of your store, and search engines are blocked. Checkout and orders from existing customers are not affected once they are in.</Text>
                <Field label={data.settings.password_set ? "New password" : "Password"} hint={pwShort ? "At least 6 characters" : data.settings.password_set ? "Leave blank to keep the current password. The password is stored hashed and can't be shown." : "At least 6 characters"}>
                  <Input type="password" autoComplete="new-password" maxLength={200} value={f.password} disabled={!writable} onChange={(e) => set({ password: e.target.value })} />
                </Field>
                <Field label="Message to your visitors"><Textarea rows={3} maxLength={500} value={f.password_message} disabled={!writable} onChange={(e) => set({ password_message: e.target.value })} /></Field>
              </>
            )}
          </div>
        </Section>
        <MovedNoticeSection writable={writable} />
      </div>
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setF(base)} onSave={doSave} saving={save.isPending} disabled={imageBad || pwMissing || pwShort} />
    </>
  )
}

type MovedNotice = { enabled: boolean; message: string; link_label: string; link_url: string; ends_at: string }

/**
 * "We've moved" banner (backend lib/moved-notice.ts): a thin bar for visitors who arrive with
 * ?moved=1 (the previous domain's redirect and the migration email links). Saved on its own.
 */
function MovedNoticeSection({ writable }: { writable: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["moved-notice"], queryFn: () => sdk.client.fetch<{ notice: MovedNotice }>("/admin/terify/settings/moved-notice") })
  const [f, setF] = useState<MovedNotice | null>(null)
  useEffect(() => {
    if (q.data) setF(q.data.notice)
  }, [q.data])
  const save = useMutation({
    mutationFn: (n: MovedNotice) => sdk.client.fetch<{ notice: MovedNotice }>("/admin/terify/settings/moved-notice", { method: "POST", body: n }),
    onSuccess: (r) => (qc.setQueryData(["moved-notice"], r), toast.success("Banner saved")),
    onError: (e) => toast.error(errorMessage(e)),
  })
  if (!f || !q.data) return null
  const set = (p: Partial<MovedNotice>) => setF({ ...f, ...p })
  const dirty = JSON.stringify(f) !== JSON.stringify(q.data.notice)
  const linkBad = f.link_url !== "" && !/^(https:\/\/|\/(?!\/))\S+$/i.test(f.link_url)
  const ended = Date.parse(`${f.ends_at}T23:59:59-05:00`) < Date.now()
  const site = storefrontUrl().replace(/\/$/, "")
  return (
    <Section title="“We’ve moved” banner">
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          A thin bar above the header, only for visitors who arrive from your previous store’s domain or a migration email. Their links end in <code className="font-mono">?moved=1</code>, for example <code className="font-mono">{site}/?moved=1</code>. Nobody else sees it, and search engines never index it.
        </Text>
        <label className="flex items-center gap-3 text-[13px]">
          <Switch checked={f.enabled} disabled={!writable} onCheckedChange={(v) => set({ enabled: v })} aria-label="Show the banner" />
          Show the banner {ended && f.enabled ? <span className="text-ui-fg-error">(the end date has passed, so it’s hidden)</span> : null}
        </label>
        <Field label="Banner text" hint={`${f.message.length} of 280 characters`}>
          <Textarea rows={2} maxLength={280} value={f.message} disabled={!writable} onChange={(e) => set({ message: e.target.value })} placeholder="Welcome! We’ve moved to a new home. Your account and subscriptions came with you." />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Link text (optional)"><Input maxLength={40} value={f.link_label} disabled={!writable} onChange={(e) => set({ link_label: e.target.value })} placeholder="Learn more" /></Field>
          <Field label="Link (optional)" hint={linkBad ? "Use a path like /account or an https:// link" : undefined}>
            <Input maxLength={300} value={f.link_url} disabled={!writable} onChange={(e) => set({ link_url: e.target.value.trim() })} placeholder="/pages/welcome" aria-invalid={linkBad} />
          </Field>
        </div>
        <Field label="Stop showing after" hint="The banner hides itself at the end of this day (US Eastern).">
          <Input type="date" value={f.ends_at} disabled={!writable} onChange={(e) => set({ ends_at: e.target.value })} className="max-w-[200px]" />
        </Field>
        {f.message ? (
          <div aria-label="Preview" className="rounded-md bg-ui-button-inverted px-4 py-2 text-center text-[13px] text-white">
            {f.message} {f.link_url ? <u className="font-semibold">{f.link_label || "Learn more"}</u> : null}
          </div>
        ) : null}
        {writable ? (
          <div className="flex justify-end">
            <Button size="small" disabled={!dirty || linkBad || (f.enabled && !f.message.trim()) || !f.ends_at} isLoading={save.isPending} onClick={() => save.mutate(f)}>Save banner</Button>
          </div>
        ) : null}
      </div>
    </Section>
  )
}
